import { useTranslation } from "react-i18next";
import type { Notification } from "../../../../../store/admin/notificationsStore";
import type { ThemeClasses } from "../../../shared";
import { formatDate } from "../../../../../utils/getDate";
import { useUsersStore } from "../../../../../store/admin/usersStore";
import { useDisableCRUD } from "../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../components/base/CRUDTooltip";
import { useUserStore } from "../../../../../store/userStore";
import { useMemo } from "react";

interface NotificationCardProps {
  notification: Notification;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  onView: () => void;
  onEdit: () => void;
}

const NOTIFICATION_ICONS = [
  {
    id: "warning-red",
    icon: "ri-error-warning-fill",
    color: "text-red-500",
    bgColor: "bg-red-500/10",
  },
  {
    id: "warning-yellow",
    icon: "ri-alert-fill",
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/10",
  },
  {
    id: "chat-blue",
    icon: "ri-chat-3-fill",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
  {
    id: "success-green",
    icon: "ri-checkbox-circle-fill",
    color: "text-primary-500",
    bgColor: "bg-primary-500/10",
  },
];

const getIconData = (icon: string) => {
  return (
    NOTIFICATION_ICONS.find((i) => i.id === icon) || {
      icon: "ri-notification-line",
      color: "text-primary-500",
      bgColor: "bg-primary-500/10",
    }
  );
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "high":
      return "bg-red-100 text-red-600";
    case "medium":
      return "bg-yellow-100 text-yellow-600";
    case "low":
      return "bg-primary-100 text-primary-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "sent":
      return "bg-primary-100 text-primary-600";
    case "scheduled":
      return "bg-blue-100 text-blue-600";
    case "failed":
      return "bg-red-100 text-red-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

export default function NotificationCard({
  notification,
  isDarkMode,
  themeClasses,
  onView,
  onEdit,
}: NotificationCardProps) {
  const { t } = useTranslation("adminSections");
  const iconData = getIconData(notification.icon_id || notification.icon);
  const users = useUsersStore((state) => state.users);
  const disableCRUD = useDisableCRUD();
  const user = useUserStore((state) => state.user);

  const canEdit = useMemo(() => {
    if (user?.role_id === "HiLYcf6ndou53xpXNJ02") {
      return true;
    }

    if (user?.role_id === "Ne6pwRmFkr4bArJN47NZ") {
      return true;
    }

    return notification.createdBy === user?.id;
  }, [notification.createdBy, user?.id]);

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-lg transition-all`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconData.bgColor}`}
          >
            <i className={`${iconData.icon} text-lg ${iconData.color}`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className={`font-semibold text-sm ${themeClasses.text} truncate max-w-[190px]`}
            >
              {notification.title}
            </h3>
            <p className={`text-xs ${themeClasses.textSecondary}`}>
              {notification.createdAt
                ? formatDate(notification.createdAt._seconds)
                : "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Image (if exists) */}
      {/* Image (if exists) or placeholder */}
      {notification.image ? (
        <div className="mb-3 rounded-lg overflow-hidden">
          <img
            src={notification.image}
            alt={notification.title}
            className="w-full h-32 object-cover"
          />
        </div>
      ) : (
        <div
          className={`mb-3 rounded-lg h-32 flex flex-col items-center justify-center border-2 border-dashed ${isDarkMode
              ? "border-dark-border bg-dark-surface"
              : "border-gray-200 bg-gray-50"
            }`}
        >
          <i
            className={`ri-image-line text-2xl ${isDarkMode ? "text-[#6A6A6A]" : "text-gray-300"
              }`}
          ></i>
          <span
            className={`text-xs mt-1 ${isDarkMode ? "text-[#6A6A6A]" : "text-gray-400"
              }`}
          >
            {t("notifications.noImage")}
          </span>
        </div>
      )}

      <div className="space-y-2 mb-4">
        <div className="flex items-start justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("notifications.fields.description")}
          </span>
          <span
            className={`text-sm ${themeClasses.text} line-clamp-2 max-w-[190px] text-right`}
          >
            {notification.description}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("notifications.fields.status")}
          </span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
              notification.status_id
            )}`}
          >
            {t(`notifications.status.${notification.status_id}`)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("notifications.fields.priority")}
          </span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
              notification.priority
            )}`}
          >
            {t(`notifications.priority.${notification.priority}`)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("notifications.fields.addresseeType")}
          </span>
          <span
            className={`text-xs ${isDarkMode ? "bg-dark-border" : "bg-gray-100"
              } p-0.5 px-2 rounded-full ${themeClasses.text}`}
          >
            {notification.type_of_addresse === "HiLYcf6ndou53xpXNJ02" || notification.type_of_addresse === "specific"
              ? t("notifications.fields.specificUsers")
              : t("notifications.fields.allUsers")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("notifications.fields.sentTo")}
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {(() => {
              const user = users.find((u) => u.id === notification.addressee);
              if (user) {
                return `${user.first_name} ${user.last_name}`;
              }
              return notification.addressee || t("notifications.types.all");
            })()}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-2 mt-3">
        <CRUDTooltip className="flex-1">
          <button
            className={`flex-1 py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors`}
            onClick={onEdit}
            disabled={disableCRUD || !canEdit}
          >
            {t("notifications.actions.edit")}
          </button>
        </CRUDTooltip>
        <button
          onClick={onView}
          className="py-2 px-3 bg-primary-500 hover:bg-primary-400 text-white rounded-lg text-sm font-medium transition-colors"
        >
          {t("notifications.actions.details")}
        </button>
      </div>
    </div>
  );
}
